import { FC, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button as AntButton,
  Modal,
  Select,
  Table,
  TableColumnsType,
} from 'antd';
import Button from '../Button';
import config from '../../lib/config';
import useAxios from '../../hooks/useAxios';
import { IGetStudents } from '../../models/students.model';
import { getStudents } from '../../api/students.api';
import Loader from '../Loader';
import ErrorResponse from '../ErrorResponse';
import { getCommonCodeLabel } from '../../lib/store';
import Pagination from '../Pagination';
import { useHistory } from 'react-router-dom';
import SelectCampus from '../SelectCampus';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import Checkbox from '../Checkbox';
import { campusHeadOffice } from '../../lib/data';
import { PERMISSIONS } from '../../constans';

const { Option } = Select;

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  selectStudent: (memberCode: string) => void;
}

const SelectStudentModal: FC<IProps> = ({
                                          visible,
                                          setVisible,
                                          selectStudent,
                                        }) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  const { data: res, isLoading, error, execute } = useAxios<IGetStudents>();

  const [field, setField] = useState('studentName');
  const [keyword, setKeyword] = useState('');
  const [includeRegistered, setIncludeRegistered] = useState(false);
  const [page, setPage] = useState(1);
  const [campusCode, setCampusCode] = useState(currentCampus);

  const ADDITIONAL_CAMPUS = PERMISSIONS.MANAGER.includes(auth?.groupNo) ? [campusHeadOffice] : undefined;

  useEffect(() => {
    doGetStudents();
  }, [campusCode, page]);

  const doGetStudents = (_page?: number) => {
    const params: any = {
      pageNo: _page || page,
      perPage: config.miniPageSize,
      campusCode,
    };

    if (field && keyword) {
      params[field] = keyword;
    }
    if (!includeRegistered) {
      params.statusCodes = 'NR';
    }

    execute(getStudents(params));
  };

  const columns: TableColumnsType<any> = [
    {
      title: t('common.student.1'),
      dataIndex: 'statusCode',
      key: 'statusCode',
      align: 'center',
      render: value => getCommonCodeLabel('MB0003', value, i18n.language),
    },
    {
      title: t('entrance.schedule.column21'),
      dataIndex: 'studentKorName',
      key: 'studentKorName',
      align: 'center',
    },
    {
      title: t('entrance.schedule.column22'),
      dataIndex: 'studentEngName',
      key: 'studentEngName',
      align: 'center',
    },
    {
      title: 'ID',
      key: 'webId',
      dataIndex: 'webId',
      align: 'center',
    },
    {
      title: t('entrance.schedule.column10'),
      key: 'schoolName',
      dataIndex: 'schoolName',
      align: 'center',
    },
    {
      title: t('entrance.schedule.column11'),
      key: 'gradeCode',
      dataIndex: 'gradeCode',
      align: 'center',
      render: value => getCommonCodeLabel('SY0006', value, i18n.language),
    },
    {
      title: t('entrance.schedule.button2'),
      key: 'memberCode',
      dataIndex: 'memberCode',
      align: 'center',
      render: value => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small pink"
            onClick={() => selectStudent(value)}
            text={t('entrance.schedule.button2')}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading ? <Loader /> : null}
      <ErrorResponse error={error} />

      <Modal
        title={t('entrance.schedule.title4')}
        width={700}
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={[
          <div className="comm-btn__area justify-content-center">
            <AntButton key="cancel" className="btn-basic gray" onClick={() => setVisible(false)}>
              {t('common.button.close')}
            </AntButton>
          </div>,
        ]}
      >
        <div>
          <div className="comm-bx__space">
            <div className="comm-btn__area right">
              <div className="w-150p">
                <SelectCampus
                  additionalCampus={ADDITIONAL_CAMPUS}
                  onChange={(value: SetStateAction<string>) => setCampusCode(value)}
                  value={campusCode}
                />
              </div>
              <div className="w-150p">
                <Select
                  className="w-100"
                  placeholder={t('common.button.select')}
                  value={field}
                  onChange={(value) => setField(value)}
                >
                  <Option value="studentName">{t('entrance.schedule.column21')}</Option>
                  <Option value="studentEngName">{t('entrance.schedule.column22')}</Option>
                  <Option value="studentWebId">ID</Option>
                </Select>
              </div>
              <div>
                <input
                  type="text"
                  className="form-control w-150p"
                  placeholder={t('common.text.input')}
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') doGetStudents(1);
                  }}
                />
              </div>
              <Button
                className="btn-basic small black"
                onClick={() => doGetStudents(1)}
                text={t('common.button.search')}
              />
              <Button
                className="btn-basic pink-line"
                onClick={() => history.push('/student/student?transfer=true')}
                text={t('entrance.schedule.button5')}
              />
            </div>
          </div>
          <div className="comm-bx__space">
            <div className="comm-btn__area right">
              <Checkbox
                checked={includeRegistered}
                onChange={() => setIncludeRegistered(!includeRegistered)}
                text={t('common.student.2')}
              />
            </div>
          </div>
          <Table
            className="comm-table__area mt-0"
            columns={columns}
            dataSource={res?.data}
            pagination={{ pageSize: config.miniPageSize }}
          />
          <div className="comm-bx__space">
            <div className="common-paging center">
              <Pagination
                count={res?.total || 0}
                page={page}
                setPage={setPage}
                mini
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SelectStudentModal;
